import type { ErrorInfo, ReactNode } from "react";
import { Component } from "react";
import { loggerService } from "@akina/infra";
import { Text, Button, Dialog } from "./atoms";

type State = {
  hasError: boolean;
  error: null | Error;
}

export class ErrorBoundary extends Component<
  {
    children: ReactNode;
    env: string;
  },
  State
> {
  private static readonly RELOAD_FLAG_KEY = "akina:reloaded-after-chunk-error";

  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error: error };
  }

  // catch potential chunk load errors when using lazy loading.
  // If a chunk load error occurs, attempt to reload the page.
  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    loggerService.error(error, errorInfo);

    const w = globalThis.window;
    if (this.isChunkLoadError(error) && w) {
      try {
        const storage = w.sessionStorage;
        const alreadyReloaded = storage.getItem(ErrorBoundary.RELOAD_FLAG_KEY) === "1";
        if (alreadyReloaded) {
          storage.removeItem(ErrorBoundary.RELOAD_FLAG_KEY);
        } else {
          storage.setItem(ErrorBoundary.RELOAD_FLAG_KEY, "1");
          console.error("Reloading APP due to chunk load error");
          w.location.reload();
        }
      } catch {
        // noop
      }
    }
  }

  // Clean up reload flag for chunk load error on successful mount of the app
  public componentDidMount(): void {
    try {
      const w = globalThis.window;
      if (!w) return;
      const storage = w.sessionStorage;
      storage.removeItem(ErrorBoundary.RELOAD_FLAG_KEY);
    } catch {
      // noop
    }
  }

  private isChunkLoadError(error: Error): boolean {
    const message = (error?.message || "").toString();
    const name = (error as { name?: string })?.name || "";
    // Common patterns across browsers and bundlers
    return /Loading chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing (a )?module( script)? failed/i.test(
      `${name}: ${message}`,
    );
  }

  public render() {
    if (this.state.hasError) {
      return (
        <Dialog open={true} withClose={false}>
          <div>
            <Text fontWeight="font-bold" size="lg" className="mb-4 text-center">
              {this.props.env === "dev" ? this.state.error?.message : "There was an error."}
            </Text>
            <Text size="sm" className="text-center">
              {this.props.env === "dev"
                ? this.state.error?.stack
                : "It looks like we are having some internal issues. Our Team has been notified."}
            </Text>
            <div className="mx-auto mt-8 w-fit">
              <Button onClick={() => globalThis.window?.location.reload()}>Reload</Button>
            </div>
          </div>
        </Dialog>
      );
    }

    return this.props.children;
  }
}
